import type { ReactNode } from "react";
import { Checkbox as AriaCheckbox } from "react-aria-components";
import { Badge } from "@/components/base/badges/badges";
import { CheckboxBase } from "@/components/base/checkbox/checkbox";
import { Dot } from "@/components/foundations/dot-icon";
import { cx } from "@/utils/cx";

/**
 * Figma Map/LegendItem entity presets (5171:58546).
 * Dot colors map to design tokens used in the Web Design System.
 *
 * Entity presets supply label + color only. Count badges are opt-in via
 * `countLabel` — never invent demo figures (e.g. `"3 / 5"`) when omitted.
 */
export const LegendItemEntity = {
	Jobs: "jobs",
	Tickets: "tickets",
	Assets: "assets",
	Records: "records",
	Job: "job",
} as const;

export type LegendItemEntity =
	(typeof LegendItemEntity)[keyof typeof LegendItemEntity];

interface LegendItemEntityPreset {
	label: string;
	/** CSS color for the legend Dot (and selected checkbox accent). */
	color: string;
}

export const LEGEND_ITEM_ENTITY_PRESETS: Record<
	LegendItemEntity,
	LegendItemEntityPreset
> = {
	[LegendItemEntity.Jobs]: {
		label: "Jobs",
		color: "var(--color-utility-blue-600)",
	},
	[LegendItemEntity.Tickets]: {
		label: "Tickets",
		color: "var(--color-utility-error-600)",
	},
	[LegendItemEntity.Assets]: {
		label: "Assets",
		color: "var(--color-utility-yellow-500)",
	},
	[LegendItemEntity.Records]: {
		label: "Records",
		color: "var(--color-utility-purple-600)",
	},
	[LegendItemEntity.Job]: {
		label: "Job",
		color: "var(--color-utility-blue-600)",
	},
};

export interface LegendItemProps {
	/**
	 * Figma Entity variant — applies label and color.
	 * Provide `entity`, or both `label` and `color`.
	 */
	entity?: LegendItemEntity;
	/** Layer label. Required when `entity` is omitted. */
	label?: string;
	/** Dot / selected-checkbox color. Required when `entity` is omitted. */
	color?: string;
	/**
	 * Count badge text (e.g. `"3 / 5"` when the layer is visible and has points).
	 * Omit or pass `null` to hide the badge — there is no default count.
	 */
	countLabel?: string | null;
	/**
	 * When true, shows a spinner ahead of `countLabel` (for in-viewport count loading).
	 */
	countLoading?: boolean;
	/** Controlled selected (layer visible) state. */
	isSelected?: boolean;
	/**
	 * Uncontrolled initial selected state.
	 * @default false
	 */
	defaultSelected?: boolean;
	/** Fires when the user toggles the checkbox. */
	onChange?: (isSelected: boolean) => void;
	isDisabled?: boolean;
	className?: string;
	/** Optional custom badge content; overrides `countLabel` / loading UI. */
	badge?: ReactNode;
}

/**
 * Single map legend row — Figma Map/LegendItem (5171:58546).
 *
 * Anatomy: Checkbox + color Dot + label + optional gray pill count Badge.
 * The count Badge is shown only when `countLabel` (or `badge`) is provided.
 */
export function LegendItem({
	entity,
	label: labelProp,
	color: colorProp,
	countLabel: countLabelProp,
	countLoading = false,
	isSelected,
	defaultSelected = false,
	onChange,
	isDisabled = false,
	className,
	badge,
}: LegendItemProps) {
	const preset = entity ? LEGEND_ITEM_ENTITY_PRESETS[entity] : undefined;
	const label = labelProp ?? preset?.label ?? "";
	const color = colorProp ?? preset?.color ?? "";

	const resolvedCountLabel = countLabelProp ?? null;
	const showBadge = badge !== undefined || resolvedCountLabel !== null;

	return (
		<AriaCheckbox
			isSelected={isSelected}
			defaultSelected={defaultSelected}
			onChange={onChange}
			isDisabled={isDisabled}
			aria-label={`Toggle ${label} layer`}
			className={(state) =>
				cx(
					"flex w-full cursor-pointer items-center gap-2",
					state.isDisabled && "cursor-not-allowed",
					!state.isSelected && "opacity-60",
					className,
				)
			}
		>
			{({
				isSelected: selected,
				isIndeterminate,
				isDisabled: disabled,
				isFocusVisible,
			}) => (
				<>
					<CheckboxBase
						size="sm"
						isSelected={selected}
						isIndeterminate={isIndeterminate}
						isDisabled={disabled}
						isFocusVisible={isFocusVisible}
						selectedColor={color}
					/>
					<Dot size="lg" className="shrink-0" style={{ color }} aria-hidden />
					<span className="min-w-0 flex-1 select-none text-xs font-bold text-secondary">
						{label}
					</span>
					{showBadge ? (
						badge !== undefined ? (
							badge
						) : (
							<Badge
								type="pill-color"
								color="gray"
								size="sm"
								className="ml-auto shrink-0"
							>
								{countLoading && selected ? (
									<span className="inline-flex items-center gap-1.5">
										<span
											className="inline-block size-3 animate-spin rounded-full border-2 border-solid border-current border-r-transparent"
											aria-hidden
										/>
										{resolvedCountLabel}
									</span>
								) : (
									resolvedCountLabel
								)}
							</Badge>
						)
					) : null}
				</>
			)}
		</AriaCheckbox>
	);
}
